import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatDuration, getFeaturedMovies } from "@/lib/movies";
import { useFavorites } from "@/hooks/useFavorites";

const INTERVAL = 7000;

export default function HeroCarousel() {
  const movies = getFeaturedMovies();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);
  const { toggleFavorite, isFavorite } = useFavorites();

  useEffect(() => {
    if (paused || movies.length < 2) return;
    const timer = window.setTimeout(() => {
      setIndex((current) => (current + 1) % movies.length);
    }, INTERVAL);
    return () => window.clearTimeout(timer);
  }, [index, paused, movies.length]);

  const active = movies[index];
  if (!active) return null;

  const goTo = (next: number) => setIndex((next + movies.length) % movies.length);

  return (
    <section
      className="relative w-full h-[86vh] min-h-[560px] md:h-[92vh] md:min-h-[680px] max-h-[900px] overflow-hidden bg-background-100 -mt-16 md:-mt-20"
      aria-label="焦点影片轮播"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStart.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStart.current === null) return;
        const diff = event.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(diff) > 50) {
          goTo(diff > 0 ? index - 1 : index + 1);
        }
        touchStart.current = null;
      }}
    >
      {movies.map((movie, movieIndex) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            movieIndex === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={movieIndex !== index}
        >
          <img
            src={movie.backdrop ?? movie.poster}
            alt={`${movie.title} 焦点影片剧照`}
            title={`${movie.title} - 韩国伦理电影在线观看`}
            className={`w-full h-full object-cover object-top ${movieIndex === index ? "animate-slow-zoom" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40"></div>
        </div>
      ))}

      <Link
        to={`/detail/${active.id}`}
        aria-label={`查看 ${active.title} 详情`}
        className="absolute inset-0 z-[5] cursor-pointer"
      ></Link>

      <div className="relative z-10 h-full flex items-center">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-shell mx-auto">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-12 items-end">
            <div key={active.id} className="max-w-2xl animate-fade-up">
              <h1 className="font-label text-[11px] md:text-xs tracking-[0.24em] md:tracking-[0.3em] text-primary-500 mb-4">
                韩国伦理电影在线观看
                <span className="hidden md:inline"> · KOREAN CINEMA ARCHIVE</span>
              </h1>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-6 px-2.5 rounded bg-primary-500 text-background-50 text-[11px] font-medium flex items-center tracking-wide">
                  本周焦点
                </span>
                <span className="font-label text-[11px] tracking-[0.3em] text-foreground-500">
                  FEATURED · {String(index + 1).padStart(2, "0")} / {String(movies.length).padStart(2, "0")}
                </span>
              </div>

              <h2 className="font-heading text-3xl md:text-5xl lg:text-[56px] leading-[1.12] text-foreground-950 font-bold text-balance">
                {active.title}
              </h2>
              <p className="mt-3 font-label text-sm tracking-[0.2em] text-primary-400">
                {active.originalTitle} · {active.year}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5 text-accent-500 font-medium">
                  <i className="ri-star-fill text-base" aria-hidden="true"></i>
                  {active.rating.toFixed(1)}
                </span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>{formatDuration(active.duration)}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>{active.ageRating}</span>
                <span className="w-1 h-1 rounded-full bg-background-400"></span>
                <span>导演 {active.director}</span>
              </div>

              <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-foreground-700 max-w-xl line-clamp-3">
                {active.synopsis}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium flex items-center gap-2 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                  立即观看
                </Link>
                <button
                  type="button"
                  onClick={() => toggleFavorite(active.id)}
                  className={`h-11 px-5 rounded-md border text-sm flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                    isFavorite(active.id)
                      ? "border-accent-500 text-accent-500 bg-accent-500/10"
                      : "border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-400"
                  }`}
                >
                  <i
                    className={isFavorite(active.id) ? "ri-bookmark-fill text-base" : "ri-bookmark-line text-base"}
                    aria-hidden="true"
                  ></i>
                  {isFavorite(active.id) ? "已加入片单" : "加入片单"}
                </button>
                <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                  <i className="ri-eye-line" aria-hidden="true"></i>
                  {(active.views / 10000).toFixed(1)} 万次浏览
                </span>
              </div>
            </div>

            <div className="hidden lg:block">
              <p className="font-label text-[11px] tracking-[0.3em] text-foreground-500 mb-3">UP NEXT</p>
              <div className="flex flex-col">
                {movies.map((movie, movieIndex) => (
                  <button
                    key={movie.id}
                    type="button"
                    onClick={() => setIndex(movieIndex)}
                    className={`group flex items-center gap-3 py-2.5 text-left border-b border-background-200/50 transition-colors cursor-pointer ${
                      movieIndex === index ? "text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    <span
                      className={`font-label text-xs w-5 ${
                        movieIndex === index ? "text-primary-500" : "text-foreground-500"
                      }`}
                    >
                      {String(movieIndex + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm truncate">{movie.title}</span>
                      <span className="block text-[11px] text-foreground-500 mt-0.5">
                        {movie.year} · {movie.ageRating}
                      </span>
                    </span>
                    <span
                      className={`w-1 h-6 rounded-full transition-colors ${
                        movieIndex === index ? "bg-primary-500" : "bg-transparent"
                      }`}
                    ></span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-5 md:bottom-8 right-4 md:right-8 flex items-center gap-3">
        <div className="flex items-center gap-1.5 mr-1">
          {movies.map((movie, movieIndex) => (
            <button
              key={movie.id}
              type="button"
              onClick={() => setIndex(movieIndex)}
              aria-label={`切换到 ${movie.title}`}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                movieIndex === index ? "w-7 bg-primary-500" : "w-3 bg-foreground-500/40 hover:bg-foreground-500/70"
              }`}
            ></button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          className="w-9 h-9 rounded-full border border-background-300/80 bg-background-50/50 backdrop-blur-sm text-foreground-800 flex items-center justify-center hover:border-primary-400 hover:text-primary-400 transition-colors cursor-pointer"
          aria-label="上一部"
        >
          <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          className="w-9 h-9 rounded-full border border-background-300/80 bg-background-50/50 backdrop-blur-sm text-foreground-800 flex items-center justify-center hover:border-primary-400 hover:text-primary-400 transition-colors cursor-pointer"
          aria-label="下一部"
        >
          <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
        </button>
      </div>
    </section>
  );
}